// Vendors
import React, { useState, useEffect } from 'react';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, SortBy, Configure } from 'react-instantsearch-dom';
import aa from 'search-insights';
import qs from 'qs';
import debounce from 'lodash/debounce';

// Components
import globalLoader from '../../site/global-loader';
import { Pagination } from '../../algolia/components';
import {
  FacetList,
  ResourceHits,
  ResultsFoundHeader,
  SearchField
} from './components';

// Helpers
import { scrollToElement } from '../../lib/utilities';

// Data
import {
  ENV,
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY
} from '../../config/index';

// User flags
const IS_EMPLOYEE =
  window.wlAlgoliaUserParams._isEmployee == '1' ? true : false;
const USER_METAS = Array.isArray(window.wlAlgoliaUserParams._userMetas)
  ? window.wlAlgoliaUserParams._userMetas
  : [];

// Build the required_user_meta filter: product is visible if it is not gated
// (is_gated_by_user_meta:false) OR its required array includes any of the
// user's enrolled keys.
const requiredUserMetaFilter = (() => {
  const clauses = [
    'is_gated_by_user_meta:false',
    ...USER_METAS.map((k) => `required_user_meta:"${k}"`)
  ];
  return `(${clauses.join(' OR ')})`;
})();

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);
window.aa = aa('init', {
  appId: ALGOLIA_APP_ID,
  apiKey: ALGOLIA_SEARCH_API_KEY
});

const environment = ENV === 'dev' ? 'local' : ENV;
const algoliaIndex = {
  main: `perc_${environment}_resource_catalog`,
  asc: `perc_${environment}_resource_catalog_replica_title_asc`,
  desc: `perc_${environment}_resource_catalog_replica_title_desc`,
  recentlyAdded: `perc_${environment}_resource_catalog_replica_recently_added`,
  mostPopular: `perc_${environment}_resource_catalog_replica_most_popular`
};

const createURL = (state) => {
  const queryParameters = {};

  if (!state) return;

  if (state.page !== 1) queryParameters.page = state.page;
  if (state.query) queryParameters.query = encodeURIComponent(state.query);
  if (state.sortBy) {
    // Abbreviate 'sortby' param (index name)..
    const sortByName = state.sortBy.replace(
      `perc_${environment}_resource_catalog`,
      ''
    );

    // ..and only write queryParam if we've selected a replica index (and abbreviate some more)
    if (sortByName !== '') {
      queryParameters.sortBy = sortByName.replace('_replica_', '');
    }
  }

  // Abbreviate facets from 'facets.pa_{facet}' to '_{facet}'
  if (state.refinementList) {
    Object.keys(state.refinementList).map((facet) => {
      if (state.refinementList[facet].length) {
        const facetName = facet.replace('facets.pa', '');
        queryParameters[facetName] = encodeURIComponent(
          state.refinementList[facet]
        );
      }
    });
  }

  const queryString = qs.stringify(queryParameters, {
    addQueryPrefix: true,
    arrayFormat: 'repeat'
  });

  return queryString ? queryString : window.location.pathname;
};

const searchStateToUrl = (searchState) =>
  searchState ? createURL(searchState) : '';

const urlToSearchState = (location) => {
  if (!location) return;

  const queryString = qs.parse(location.search.slice(1));
  const { query = '', page = 1, sortBy = algoliaIndex.main } = queryString;
  const refinementList = {};
  let sortByParsed = sortBy;

  // Rename sortBy to proper index name so app state can be updated (only applies to replicas)
  if (queryString.sortBy)
    sortByParsed = `perc_${environment}_resource_catalog_replica_${sortBy}`;

  // Rename facets to proper facet names so app state can be updated
  Object.keys(queryString).map((facet) => {
    if (facet.charAt(0) === '_') {
      const facetString = () =>
        queryString[facet].includes('&amp;')
          ? queryString[facet].replace('&amp;', '&')
          : queryString[facet];

      refinementList[`facets.pa${facet}`] = decodeURIComponent(
        facetString()
      ).split(',');
    }
  });

  return {
    query: decodeURIComponent(query),
    page,
    sortBy: sortByParsed,
    refinementList
  };
};

const updateHistory = debounce(
  (updatedSearchState) =>
    window.history.pushState(
      searchStateToUrl(updatedSearchState),
      updatedSearchState,
      searchStateToUrl(updatedSearchState)
    ),
  500,
  { leading: true, trailing: true }
);

const ResourceCatalog = () => {
  const { location } = window;
  const [searchState, setSearchState] = useState({});
  const [catalogIsReady, setCatalogIsReady] = useState(false);
  const onSearchStateChange = (updatedSearchState) => {
    setSearchState(updatedSearchState);
    updateHistory(updatedSearchState);
    scrollToElement('#search-section');
  };

  useEffect(() => {
    globalLoader.show();

    let loaded = new Promise((resolve, reject) => {
      if (searchClient) resolve(searchClient);

      return reject(
        new Error(
          'Resource Catalog search client failed in loading. Please refresh the page to try again.'
        )
      );
    });

    loaded.then(() => {
      setSearchState(urlToSearchState(location));
      setCatalogIsReady(true);

      setTimeout(() => {
        scrollToElement('#search-section');
        globalLoader.hide();
      }, 1000);
    });
  }, []);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={algoliaIndex.main}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
    >
      <Configure
        hitsPerPage={15}
        filters={[
          !IS_EMPLOYEE ? 'NOT perc_employees_only:true' : '',
          requiredUserMetaFilter
        ]
          .filter(Boolean)
          .join(' AND ')}
        clickAnalytics
      />
      {catalogIsReady && (
        <>
          <div className="grid">
            <div className="grid__col-xs-12 grid__col-sm-6 grid__col-md-5 grid__col-lg-4 grid__col-xl-3">
              <aside className="resource-sidebar">
                <div className="callout-search__search">
                  <SearchField />
                </div>
                <div className="resource-sidebar__facets">
                  <FacetList />
                </div>
              </aside>
            </div>

            <div className="grid__col-xs-12 grid__col-sm-6 grid__col-md-7 grid__col-lg-8 grid__col-xl-9">
              <div className="grid grid--middle">
                <div className="grid__col-xs-7 grid__col-sm-12 grid__col-md-7 grid__col-lg-9">
                  <ResultsFoundHeader />
                </div>
                <div className="grid__col-xs-5 grid__col-sm-12 grid__col-md-5 grid__col-lg-3">
                  <form className="related-content__filters">
                    <div className="form__field">
                      <SortBy
                        defaultRefinement={algoliaIndex.main}
                        items={[
                          {
                            value: algoliaIndex.main,
                            label: 'Sort By'
                          },
                          {
                            value: algoliaIndex.asc,
                            label: 'A-Z'
                          },
                          {
                            value: algoliaIndex.desc,
                            label: 'Z-A'
                          },
                          {
                            value: algoliaIndex.recentlyAdded,
                            label: 'Recently Added'
                          },
                          {
                            value: algoliaIndex.mostPopular,
                            label: 'Most Popular'
                          }
                        ]}
                        className="form__select form__select--alt form__select--lg"
                      />
                    </div>
                  </form>
                </div>
              </div>

              <ResourceHits />

              <div className="section--sm u--padding-bottom-0">
                <Pagination padding={4} />
              </div>
            </div>
          </div>
        </>
      )}
    </InstantSearch>
  );
};

export default ResourceCatalog;
